/**
 * Norðurljós — how strong, how high, and what colour (T114, **D61**, **D62**).
 *
 * `weather.ts` and `moon.ts` are the models this follows: **all the arithmetic
 * that can be wrong lives here, where it can be tested**, and the drawing lives
 * in the client. Nothing in this file touches three.js or a canvas.
 *
 * ## Visibility is the product of three things, and we already had two
 *
 * | | Where it comes from |
 * |---|---|
 * | **Darkness** | the sun's elevation, from the clock |
 * | **A clear sky** | `cloud` in oktas, from the Veðurstofa archive |
 * | **Activity** | the Kp archive — the only new ingredient (**D61**) |
 *
 * 🔴 **Iceland sits UNDER the auroral oval, so there is no on/off threshold.**
 * The quiet oval sits at about 66–68° geomagnetic latitude and moves roughly 2°
 * equatorward per Kp step; Iceland is inside it at rest. A quiet night still
 * shows something low in the north.
 *
 * 🎯 **haugur, 2026-08-12, choosing how a faint one should look:** *"Make it
 * visible, its a cool effect that people associate with iceland strongly."*
 * ⚠️ **This is a deliberate departure from realism and it is his call.** To the
 * naked eye a weak aurora is nearly colourless — a pale grey smudge people
 * mistake for cloud. **Ours is green even when weak.** The difference between a
 * quiet night and a good one is therefore carried by **how high it reaches, how
 * much of the sky it fills and whether it moves**, not by drained colour.
 *
 * ## ⚠️ What is measured here and what is ours
 *
 * **Measured:** the colours, sampled from **twenty** reference frames haugur
 * supplied (see `AURORA_GREEN` below) — though **the shipped green is his pick
 * from a sample sheet, not the measurement**; both numbers are recorded there.
 * **Ours, and tunable:** every ramp — how activity
 * maps to brightness, how high the band sits, when purple appears. The oval's
 * position and its ~2°-per-Kp-step migration are real; *how bright we draw it*
 * is a game decision and is not a measurement.
 */

/**
 * 🔴 **The aurora's own green: hue 140°, measured across TWENTY reference frames
 * on 2026-08-12**, weighted by how much of each frame the green covers — area
 * being the best proxy available for *"this really is aurora and not something
 * else in the picture"*.
 *
 * 🔴 **The spread is the finding, and it is why one photograph was not enough.**
 * The frames are genuinely bimodal: one family sits at **hue 102–113** (a
 * yellow-green, chartreuse) and another at **151–164** (emerald). Both are real
 * photographs of real aurora; the difference is camera processing, and **either
 * one taken alone would have been confidently wrong.**
 *
 * ⚠️ **An earlier version of this file said hue 155°, from `nightsky1.jpg`
 * alone.** haugur then supplied fifteen more frames the same afternoon and the
 * weighted answer moved 15° — easily enough to see.
 *
 * ✅ **And the physics agrees with the photographs, which is the reassuring
 * part.** The aurora's green is the oxygen line at **557.7 nm**, and that
 * wavelength converts to roughly **hue 134°** in sRGB. A measurement landing
 * independently at 140° is corroboration this project rarely gets.
 *
 * ⚠️ **This also corrects Design §12.6a's "hue 169°"** — that was the median of
 * the *whole sky* with the aurora mixed into it, which is a different and equally
 * correct measurement of a different thing.
 *
 * 🔴 **AND THEN haugur CHOSE 120°, NOT 140°, AND THAT IS WHAT SHIPS.** He was
 * shown six hues spanning both families with everything else held identical and
 * picked a pure green, 20° off the measured mean and 14° off the emission line.
 *
 * ⚠️ **This is a preference overriding a measurement, and it is recorded as one
 * rather than quietly folded into the number.** It is the same call as *"make it
 * visible"* above: he is not claiming 120° is what the sky does, he is choosing
 * what he wants to look at. **The measurement is still written down here**, so a
 * future session can move it back without redoing the work — and so nobody
 * mistakes the shipped value for a finding.
 */
export const AURORA_GREEN = '#21d921';

/**
 * The body of the curtain rather than its core — the same hue, drained of
 * saturation. Weaker, greyer, and what most of the *area* of a curtain actually
 * is; the core colour above is the bright lower edge only.
 */
export const AURORA_GREEN_BODY = '#5d8c5d';

/**
 * The purple fringe: **hue 305°**, the area-weighted mean across the same twenty
 * frames.
 *
 * ⚠️ **This family spreads even wider than the green — 263° to 332°** — and the
 * two ends are different things. The **violet** end (263–280°) is nitrogen; the
 * **pink-magenta** end (327–332°) is the high red oxygen line at 630 nm sitting
 * *on top of* the green and mixing with it. **305° is between them**, which is
 * both the honest average and, conveniently, what people mean when they say the
 * aurora went purple.
 *
 * 🎯 **haugur's answer on 2026-08-12: green is the body, purple is a fringe on
 * the top edge at high activity only.** The measurements support it — the frames
 * are strongly one or the other and rarely both, and the purple-dominant ones are
 * the rarer kind.
 */
export const AURORA_PURPLE = '#8c4c87';

/**
 * ⚠️ **V22 — a photograph's HUE is evidence; its VALUE is not.**
 *
 * Both reference frames are **long exposures**, and an aurora photograph is the
 * worst case for this: the camera gathers for several seconds what the eye sees
 * in one instant. The measured core sits at value **0.82**, which is brighter
 * than our *overcast daylight* sky and plainly impossible at night.
 *
 * The sunset work scaled its measured stops to **62%** for the same reason. This
 * scales less — **90%**, haugur's pick from six lettered samples — because he
 * explicitly chose visible over faithful.
 * ⚠️ **This constant is the one to move if it looks like a neon sign on screen**,
 * and it is exposed in Dev Tools for exactly that reason.
 */
export const PHOTOGRAPH_VALUE_SCALE = 0.9;

/** What the sky needs to know to draw norðurljós. */
export interface AuroraConditions {
  /** The planetary index, 0–9, from the archived year (**D61**). */
  readonly kp: number;
  /** The sun's elevation in degrees; negative is below the horizon. */
  readonly sunElevation: number;
  /** Cloud in oktas, 0–8. ⚠️ **9 means SKY OBSCURED (fog)**, not "more than 8". */
  readonly cloud: number;
}

/** Everything the renderer needs, and nothing about how to draw it. */
export interface AuroraLook {
  /** 0–1. **Zero means draw nothing at all** — not a faint nothing, nothing. */
  readonly strength: number;
  /** 0–1 from Kp alone, before darkness and cloud are applied. */
  readonly activity: number;
  /** 0–1 — how dark the sky is. */
  readonly darkness: number;
  /** 0–1 — how much gets through the cloud. */
  readonly clarity: number;
  /** How high the arch peaks, due north, in degrees. */
  readonly archApexDegrees: number;
  /**
   * How far north the arch's apex is from overhead — `90 − apex`. Kept because
   * it is the number the auroral-oval reasoning is expressed in.
   */
  readonly spineOffDegrees: number;
  /** Half the ribbon's width, in degrees. A sheet seen edge-on is thin. */
  readonly halfWidthDegrees: number;
  /** How far the spine wanders along its length — haugur's "snake". */
  readonly snakeDegrees: number;
  /** How far the rays reach up from the arch's bright lower edge, in degrees. */
  readonly rayHeightDegrees: number;
  /** 0–1 — how much purple to blend into the top edge. */
  readonly purple: number;
  /** 0–1 — flat glow at 0, sharp vertical rays at 1. */
  readonly rayContrast: number;
  /** How long one drift cycle takes, in **real** seconds. See below. */
  readonly driftSeconds: number;
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * How bright the display is, from Kp alone.
 *
 * ⚠️ **The floor is deliberate and it is not zero.** Kp 0 gives **0.25** rather
 * than nothing, because Iceland is inside the oval even at rest — and because
 * haugur asked for it to be visible. A night with nothing in the sky at all
 * should come from *cloud* or *daylight*, which are the honest reasons, not from
 * a quiet sun.
 *
 * The curve is deliberately not linear: the step from Kp 0 to Kp 3 is worth more
 * than the step from Kp 6 to Kp 9, because by Kp 6 the sky is already full.
 */
export function activityFromKp(kp: number): number {
  const k = clamp01(kp / 9);
  return clamp01(0.25 + 0.75 * Math.pow(k, 0.75));
}

/**
 * How dark it is, from the sun's elevation.
 *
 * **Nothing above −6°** (the end of civil twilight — the sky is still blue and
 * the aurora is washed out), **full below −12°** (the end of nautical twilight).
 *
 * 🔴 **This is what stops the aurora appearing in June, and it is correct that it
 * does.** At 64°N the summer sun bottoms out near −3°, so an Icelandic June night
 * never reaches −6° at all. **You cannot see norðurljós in Iceland in June**, and
 * this falls out of the arithmetic rather than being special-cased.
 */
export function darknessFromSun(sunElevation: number): number {
  return clamp01((-sunElevation - 6) / 6);
}

/**
 * How much survives the cloud.
 *
 * 🔴 **The aurora gets no exemption.** D59 lets the sun keep 30% and the moon 15%
 * through cloud, so you can tell where they are — 🎯 *"even when its cloude i want
 * to be at least make out where it is."* **That reasoning does not extend here:**
 * the aurora is about 100 km up, far above any weather, and eight oktas of
 * Icelandic stratus hides it completely. Drawing a ghost of one through overcast
 * would be inventing something nobody has ever seen.
 *
 * ⚠️ **Cloud 9 is fog, not nine oktas.** It returns 0, like full overcast.
 */
export function clarityFromCloud(cloud: number): number {
  if (cloud >= 9) return 0;
  return clamp01(1 - cloud / 8);
}

/**
 * How high the curtain sits, in degrees above the horizon.
 *
 * ⚠️ **This is an observer's rule of thumb, not a physical model of the oval**,
 * and it is marked so rather than dressed up. What every Icelandic aurora guide
 * tells you — and what the reference photographs show — is: **on a quiet night
 * look low to the north; on an active night it is overhead and everywhere.**
 *
 * A true model would place the oval by geomagnetic latitude and work out the
 * apparent elevation from the aurora's ~110 km altitude and its distance. That
 * is a real thing we could build later; it would move the band *past* the zenith
 * to the southern sky at high Kp, which is genuinely what happens and which
 * nobody would believe if they saw it without being told.
 */
export function baseDegreesFromKp(kp: number): number {
  /* ⚠️ **The floor is 5°, not 0°, and the preview is what found it.** A band
     based at 2° sat entirely behind the ridge line — and a real horizon in
     Iceland is hills, not a sea edge, so a quiet display placed that low is one
     nobody would ever see. 5° clears ordinary ground from most places. */
  /* ⚠️ **3.2° per Kp step, not 5.2.** The steeper original put the Kp 9 base at
     52°, which left almost no room under `HIGHEST_TOP_DEGREES` for the curtain
     to be tall — a strong night came out as a high thin stripe. **Height is what
     makes a strong night read as strong**, so the base climbs more gently and
     leaves it the room. */
  return 5 + 3.2 * clamp01(kp / 9) * 9;
}

/**
 * 🔴 **THE RIBBON'S GEOMETRY — read this before changing any number below.**
 *
 * 🎯 **haugur, 2026-08-12, after three rounds of the old model:** *"when i look
 * at aurora, for example it comes from the west and snakes to the east, i don't
 * get a circular vibe from it."* He then chose **option C**.
 *
 * ## Why the old model could never work
 *
 * The first version described the aurora as **elevation as a function of
 * bearing** — a band sitting at some height across some sector of the north.
 *
 * 🔴 **That is a ring by construction.** Seen from the inside, a line at constant
 * elevation *is* a circle centred on the viewer. Narrowing it from 200° to 110°
 * made the circle smaller. Making its height wander made the circle wobble. **No
 * amount of tuning removes a circle from a definition that is one.**
 *
 * ## What replaces it
 *
 * **The ribbon lies near a GREAT CIRCLE** — a line that goes over the top of the
 * sky rather than round the side of it. Two angles describe every direction:
 *
 * | | |
 * |---|---|
 * | **`along`** | where you are **on** the circle: **0° at the eastern horizon, 90° overhead, 180° at the western horizon** |
 * | **`off`** | how far you are **from** it, in degrees toward the north |
 *
 * ✅ **`off` is also the whole activity model, for free and for the right
 * reason.** The locus of a constant `off` is an **arch centred on the north point
 * of the horizon**: at `off = 0` it passes straight overhead; at `off = 58` it is
 * a low arch peaking 32° up in the north. **That is exactly what the auroral oval
 * does** — it sits far north when the sun is quiet and moves overhead as activity
 * rises. So one number carries both the geometry and the physics.
 *
 * ⚠️ **`off` is kept well short of 90°.** At 90° every direction converges on the
 * north point and `along` becomes meaningless — the same degeneracy that made the
 * old model's rays converge at the zenith, moved to a different pole. **It is a
 * property of spherical coordinates, not a bug that was fixed once.**
 */

/** Where the ribbon can sit, in degrees off the overhead crossing. */
export const AURORA_OFF_MIN = -18;
export const AURORA_OFF_MAX = 78;

/** The ribbon runs from one horizon to the other: 0° east, 90° up, 180° west. */
export const AURORA_ALONG_SPAN = 180;

/**
 * How far north the ribbon's spine sits, in degrees off the overhead crossing.
 *
 * **Kp 0 → 58°**, a low arch peaking about 32° up in the north — what you see on
 * an ordinary clear night. **Kp 9 → 6°**, all but straight overhead.
 *
 * ⚠️ **The banding is ours; the direction of travel is not.** The quiet oval sits
 * at ~66–68° geomagnetic latitude and moves ~2° equatorward per Kp step, with
 * Iceland inside it at rest. **Ours moves the same way for the same reason**, but
 * how far per step is a game decision and is not a measurement.
 */
export function spineOffFromKp(kp: number): number {
  return 90 - archApexFromKp(kp);
}

/** How wide the arc is, either side of north. Wide, because real arcs are. */
export const ARCH_HALF_SPAN_DEGREES = 84;

/**
 * How high the arch peaks, due north, in degrees.
 *
 * **Kp 0 → 14°**, a low arc you might miss. **Kp 9 → 48°**, filling the northern
 * half of the sky.
 *
 * ⚠️ **Kept well clear of the zenith on purpose.** The rays rise from here, and
 * anything that reaches straight up converges on a point — the fault that has now
 * been found twice, at two different poles.
 */
export function archApexFromKp(kp: number): number {
  /* ⚠️ Lowered from `14 + 34` when the zenith test caught the sum of apex, snake
     and ray height reaching 85° — close enough to overhead for the rays to
     converge, which is the fault this whole rebuild exists to remove. */
  return 13 + 25 * clamp01(kp / 9);
}

/**
 * Half the ribbon's width, in degrees.
 *
 * ⚠️ **Narrow.** A real curtain is a *sheet* seen nearly edge-on — thin across
 * and enormously long. **Width is what made the old model read as a wall**, and
 * the temptation to widen it should be resisted: length and movement are what
 * make a strong night look strong.
 */
export function ribbonHalfWidthFromKp(kp: number): number {
  /* ⚠️ Widened from `4.5 + 7.5` once the build could be held against the sketch
     it was approved from: the real thing came out visibly thinner than the
     picture haugur said yes to. **That comparison is the whole reason the sketch
     was kept in the repository rather than thrown away.** */
  return 5.5 + 9 * clamp01(kp / 9);
}

/**
 * How far the rays reach **up** from the arch's bright lower edge, in degrees.
 *
 * ⚠️ **Upward, and only upward.** A curtain hangs from nothing: its lower border
 * is where the electrons stop, and everything above it is the sheet lit along the
 * field lines. There is no aurora below the arc.
 */
export function rayHeightFromKp(kp: number): number {
  return 13 + 15 * clamp01(kp / 9);
}

/**
 * How far the spine wanders from side to side along its length, in degrees.
 *
 * 🎯 **This is haugur's "snake".** A ribbon that runs dead straight is a painted
 * arc; the wander is what makes it read as something hanging in the air.
 */
export function snakeDegreesFromKp(kp: number): number {
  return 5 + 9 * clamp01(kp / 9);
}

/**
 * How much purple to put on the top edge.
 *
 * **Nothing below Kp 4**, rising to 0.6 at Kp 9. 🎯 haugur's answer, 2026-08-12.
 * Purple comes from nitrogen high in the atmosphere and needs a strong event to
 * show; drawing it on quiet nights would read as fantasy rather than as Iceland.
 */
export function purpleFromKp(kp: number): number {
  return clamp01((kp - 4) / 5) * 0.6;
}

/**
 * Flat glow, or sharp vertical rays.
 *
 * The rays are the thing that makes it read as **aurora** rather than as green
 * fog — 🔴 **V21's lesson applied to a second phenomenon**: a diffuse glow spread
 * evenly is not an event. A weak display really is a smooth featureless arc, so
 * this starts low and climbs.
 */
export function rayContrastFromKp(kp: number): number {
  return clamp01(0.15 + (0.8 * kp) / 9);
}

/**
 * How long one drift cycle takes — **in real seconds, not game seconds.**
 *
 * 🔴 **This is the one place the aurora deliberately ignores the game clock, and
 * it is haugur's call.** A game day lasts 20 minutes, so one game hour passes in
 * **50 real seconds**. Anything driven by game time moves 72× too fast, and a
 * curtain doing that is a strobe rather than a sky.
 *
 * **So: the STRENGTH follows game time** — it swells and fades across a game
 * night as the archived Kp changes every three game hours — **while the SHAPE
 * drifts on real seconds**, at the pace it does when you stand outside and watch.
 *
 * Quiet displays barely move; strong ones can change visibly in under a minute.
 */
export function driftSecondsFromKp(kp: number): number {
  return 180 - 145 * clamp01(kp / 9);
}

/**
 * Everything the sky needs, from the three ingredients.
 *
 * ⚠️ **`strength` is a product, so any one of the three being zero draws
 * nothing.** That is the intended behaviour: daylight, thick cloud, or (never,
 * in practice) a dead sun each individually cancel the display.
 */
export function auroraLook(conditions: AuroraConditions): AuroraLook {
  const activity = activityFromKp(conditions.kp);
  const darkness = darknessFromSun(conditions.sunElevation);
  const clarity = clarityFromCloud(conditions.cloud);

  return {
    strength: activity * darkness * clarity,
    activity,
    darkness,
    clarity,
    archApexDegrees: archApexFromKp(conditions.kp),
    spineOffDegrees: spineOffFromKp(conditions.kp),
    halfWidthDegrees: ribbonHalfWidthFromKp(conditions.kp),
    snakeDegrees: snakeDegreesFromKp(conditions.kp),
    rayHeightDegrees: rayHeightFromKp(conditions.kp),
    purple: purpleFromKp(conditions.kp),
    rayContrast: rayContrastFromKp(conditions.kp),
    driftSeconds: driftSecondsFromKp(conditions.kp),
  };
}

/**
 * How bright the ribbon is **across its width**, where `0` is its bright lower
 * edge and `1` is the far side that fades out.
 *
 * 🔴 **One edge is hard and luminous; everything past it fades.** That is real —
 * the lower border is where the electrons stop — and it is the single most
 * obvious way to draw an aurora wrong. Look at `nightsky1.jpg`: the bottom of
 * the curtain is a line, the top is a suggestion.
 *
 * ⚠️ **Renamed from `curtainProfile` when the model became a ribbon (option C).**
 * The function is unchanged; what moved is the meaning of its argument. It used
 * to run up a band from the horizon, and now it runs **across a sheet** — from
 * the edge nearest the ground toward the edge nearest overhead.
 */
export function ribbonProfile(across: number): number {
  if (across < 0 || across > 1) return 0;
  /* A quick rise into the bright edge, then a long fade across. */
  const rise = Math.min(1, across / 0.06);
  return rise * Math.pow(1 - across, 1.6);
}

// ── drawing it ──────────────────────────────────────────────────────────────
//
// 🔴 **The painter lives HERE, in shared, and both the preview tool and the game
// call it.**
//
// The alternative — maths in shared, drawing in the client — is what this project
// would normally do, and it is wrong for this one thing. **haugur approved a
// picture.** If the game draws the curtain with a second implementation, the
// thing he approved and the thing that ships are two different programs that
// merely started out agreeing, and nothing here can see the screen to notice when
// they stop. **One painter means the PNG is evidence about the game, not evidence
// about the PNG.**
//
// ⚠️ **It fills a texture rather than drawing to a canvas**, so it depends on
// nothing: no three.js, no DOM, no Node. The client wraps the bytes in a
// `DataTexture` and the preview composites them into a panel.

/**
 * 🔴 **The texture's axes: bearing across, elevation up.**
 *
 * ⚠️ **They were `along`/`off` for exactly one build, and haugur saw why that was
 * wrong within a minute:** *"looks kinda like it bends the wrong way."*
 *
 * Painting in the ribbon's own frame put the curtain's **width** across the arch
 * — which is vertical at the arch's apex and **horizontal down at its legs**. So
 * the rays rotated as they went, fanning outward like a starburst.
 *
 * 🔴 **Real aurora rays are vertical, everywhere along the arc.** They follow the
 * magnetic field, which at Iceland's latitude is near-vertical, and that is why
 * every photograph shows parallel vertical rays whether the arc is overhead or
 * away to the north.
 *
 * ✅ **So the ARCH comes from the great circle and the RAYS stay vertical.** The
 * baseline is derived from the same geometry as before — an arch centred on the
 * north point, descending to the horizon at both ends — and the curtain then
 * rises **vertically** from it. That keeps what option C fixed and drops what it
 * broke.
 */
export const AURORA_BEARING_SPAN = 180;
export const AURORA_ELEVATION_SPAN = 90;

/** An RGB triple, 0–255. */
export type Rgb = readonly [number, number, number];

const hexToRgb = (hex: string): Rgb => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
];

export interface AuroraPaintOptions {
  readonly width: number;
  readonly height: number;
  /**
   * How far through the drift, in turns. **Advance this by
   * `elapsedRealSeconds / look.driftSeconds`** — real seconds, never game
   * seconds, or the curtain strobes 72× too fast.
   */
  readonly phase: number;
  /** Usually `PHOTOGRAPH_VALUE_SCALE`. Separate so a sheet can compare values. */
  readonly valueScale: number;
  /** Overrides, used only by the "which green" sample sheet. */
  readonly core?: Rgb;
  readonly body?: Rgb;
  readonly purple?: Rgb;
}

/**
 * Smooth pseudo-noise, 0–1.
 *
 * Three sine waves at frequencies that do not divide into each other, so the
 * pattern does not visibly repeat across the sky. **Not a real noise function**,
 * and it does not need to be: what it has to produce is *"do the rays read as
 * rays"*.
 */
function wave(x: number, phase: number): number {
  const a = Math.sin(x * 0.052 + phase);
  const b = Math.sin(x * 0.131 + phase * 1.7 + 2.1);
  const c = Math.sin(x * 0.317 + phase * 0.6 + 4.3);
  return (a * 0.5 + b * 0.34 + c * 0.16) * 0.5 + 0.5;
}

/**
 * The elevation of the arch's bright lower edge at a given bearing, or `null`
 * where the arch does not reach.
 *
 * 🔴 **This is the geometry option C was built for, kept.** The arch is the set
 * of directions a fixed angle away from the east–overhead–west great circle,
 * which makes it **a curve centred on the north point that descends to the
 * horizon at both ends** — a real arc across the northern sky, not a band at one
 * height encircling the viewer.
 *
 * ⚠️ **Parametrised directly, and NOT from the great circle after all.** The
 * great-circle form ties the apex's height to the arc's width — a low arc is
 * forced to be narrow, a wide one to reach the zenith — and haugur's first
 * option-C build showed what that costs: at Kp 7 the apex sat 72° up and the
 * rays converged overhead all over again.
 *
 * 🔴 **A real auroral arc is LOW AND WIDE**: it spans most of the northern sky
 * and peaks perhaps 15–50° up. That is not a small circle round the viewer, and
 * it is not a curve any tidy formula hands you — so it is chosen, and said to be
 * chosen.
 *
 * ⚠️ **`null` outside its span**, rather than a clamped value: an arch that runs
 * on past where it ends is the flat green wall all over again.
 */
export function archElevationAt(bearingDegrees: number, apexDegrees: number): number | null {
  const half = ARCH_HALF_SPAN_DEGREES;
  const t = Math.abs(bearingDegrees) / half;
  if (t >= 1) return null;
  /* A gentle parabola: highest due north, descending to the horizon at ±half. */
  const e = apexDegrees * (1 - t * t);
  return e <= 0 ? null : e;
}

/**
 * Paint the aurora into an RGBA buffer.
 *
 * **`u` is the bearing** across `AURORA_BEARING_SPAN`, centred on north.
 * **`v` is the elevation**, zenith at the top of the texture.
 *
 * 🔴 **The arch is baked into the pixels; the rays are vertical.** That
 * combination is the whole point — see the note on the axes above. The curve
 * comes from the great-circle geometry, and the rays run straight up from it the
 * way the magnetic field really runs.
 *
 * 🔴 **Alpha carries the intensity and the RGB carries the colour**, because the
 * aurora is drawn with **additive** blending — light arriving, not a sticker over
 * the sky, which is why stars stay visible through a weak one exactly as they do
 * in `nightsky1.jpg`.
 */
export function paintAuroraTexture(look: AuroraLook, options: AuroraPaintOptions): Uint8Array {
  const { width, height, phase, valueScale } = options;
  const data = new Uint8Array(width * height * 4);
  if (look.strength <= 0) return data;

  const core = options.core ?? hexToRgb(AURORA_GREEN);
  const body = options.body ?? hexToRgb(AURORA_GREEN_BODY);
  const purple = options.purple ?? hexToRgb(AURORA_PURPLE);

  const slide = phase * 2 * Math.PI;

  for (let px = 0; px < width; px++) {
    const bearing = (px / width - 0.5) * AURORA_BEARING_SPAN;

    /**
     * 🎯 **The snake**, applied to how far north the arch sits, so the arc
     * wanders as it crosses instead of being a drawn-compass curve.
     */
    const b = (bearing * Math.PI) / 180;
    const snake =
      (Math.sin(b * 2.3 + 0.7 + slide) * 0.58 +
        Math.sin(b * 4.1 + 2.3 - slide * 0.6) * 0.27 +
        Math.sin(b * 1.3 - 1.4) * 0.15) *
      look.snakeDegrees;

    const base = archElevationAt(bearing, look.archApexDegrees + snake);
    if (base === null) continue;

    /* Fade out where the arch meets the ground rather than stopping dead. */
    const ends = Math.min(1, Math.max(0, base / 6));

    /* Rays: vertical stripes, varying with bearing only. */
    const rays =
      1 - look.rayContrast + look.rayContrast * Math.pow(wave(px * 2.6 + slide * 24, 0.4), 1.7);

    /* Gaps, so it is not an unbroken painted arc. */
    const envelope = Math.max(0.14, wave(px * 0.55 + slide * 8, 1.3) * 1.3 - 0.22);

    const column = ends * rays * envelope * look.strength;
    if (column <= 0.001) continue;

    for (let py = 0; py < height; py++) {
      const elevation = AURORA_ELEVATION_SPAN * (1 - py / height);
      /* 🔴 Up from the arch, never down. A curtain hangs from nothing. */
      const across = (elevation - base) / look.rayHeightDegrees;
      const profile = ribbonProfile(across);
      if (profile <= 0.002) continue;

      const bodyMix = Math.min(1, Math.max(0, (across - 0.35) / 0.65));
      let r = core[0] + (body[0] - core[0]) * bodyMix;
      let g = core[1] + (body[1] - core[1]) * bodyMix;
      let bl = core[2] + (body[2] - core[2]) * bodyMix;

      const fringe = look.purple * Math.max(0, (across - 0.45) / 0.55);
      if (fringe > 0) {
        r += (purple[0] - r) * fringe;
        g += (purple[1] - g) * fringe;
        bl += (purple[2] - bl) * fringe;
      }

      const intensity = Math.min(1, profile * column * valueScale);
      const q = (py * width + px) * 4;
      /* eslint-disable-next-line security/detect-object-injection --
         `q` is `(py * width + px) * 4` with both loop counters bounded by the
         buffer's own dimensions, so it cannot leave the array. */
      data[q] = Math.round(r);
      data[q + 1] = Math.round(g);
      data[q + 2] = Math.round(bl);
      data[q + 3] = Math.round(intensity * 255);
    }
  }

  return data;
}
